import type { AnyActorRef } from 'xstate';
import { enqueueActions, setup } from 'xstate';
import { ALERT_VIEW_STATE, DOCUMENTS_SERVICE } from '~/features/xstateSystem';
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from '../../documents/constants';
import {
  INSURANCE_CERTIFICATE_UPLOAD,
  PARSE_EXTENSIONS_BY_WORKFLOW,
} from '../../documents/parseWorkflow';
import type { DocumentInfoValues } from '../../documents/types';
import { getUploadErrorAlert, getUploadRejection } from '../../documents/utils';

const DOCUMENTS_SERVICE_IDLE = '$_IDLE';

// Insurance certificates are read as PDF only (MCV-1760), so that is all this upload takes.
const CERTIFICATE_EXTENSIONS = PARSE_EXTENSIONS_BY_WORKFLOW['insurance-parse'];

type CertificateUploadEvent =
  | { type: 'ON_UPLOAD_CERTIFICATE'; payload: { file: File; providerId?: number } }
  | { type: 'ON_DOCUMENT_UPLOADED'; documentReference: { id: number; docName: string } }
  | { type: 'ON_DOCUMENT_UPLOAD_ERROR'; error: Error }
  | { type: 'ON_UPLOAD_REFUSED' };

const optionFor = (options: { label: string; value: string }[], value: string) =>
  options.find((option) => option.value === value) ?? { label: value, value };

/**
 * The server promotes a certificate in the malpractice insurance category to an insurance-parse
 * job on upload, so the metadata is all the parse trigger this upload needs.
 */
export const buildCertificateUploadValues = (file: Pick<File, 'name'>): DocumentInfoValues => ({
  docCategory: optionFor(DOCUMENT_CATEGORIES, INSURANCE_CERTIFICATE_UPLOAD.docCategory),
  docType: optionFor(DOCUMENT_TYPES, INSURANCE_CERTIFICATE_UPLOAD.docType),
  docName: file.name,
  docDesc: '',
});

type Alert = { text: string; subText?: string; type: 'error' | 'success'; timeout: number };

const NOT_READY_ALERT: Alert = {
  text: 'Upload not ready',
  subText: 'Your documents workspace is still loading. Please wait a moment and try again.',
  type: 'error',
  timeout: 5000,
};

const getDocumentsService = (system: { get: (id: string) => unknown }) =>
  system.get(DOCUMENTS_SERVICE) as AnyActorRef | undefined;

const sendAlert = (system: { get: (id: string) => unknown }, payload: Alert) => {
  try {
    (system.get(ALERT_VIEW_STATE) as AnyActorRef | undefined)?.send({
      type: 'ON_SHOW_ALERT',
      payload,
    });
  } catch (error) {
    console.error('[INSURANCE] Failed to send alert:', error);
  }
};

/**
 * Uploads an insurance certificate through the documents service's UPLOAD_DOCUMENT, the same path
 * the vault Documents page uses. It is the `viewRef` the service reports the upload outcome to.
 * The service does not alert a failed upload; this machine does, with the API's reason.
 */
export const certificateUploadMachine = setup({
  types: {
    context: {} as Record<string, never>,
    events: {} as CertificateUploadEvent,
  },
  guards: {
    isRejectedFile: ({ event }) =>
      event.type === 'ON_UPLOAD_CERTIFICATE' &&
      getUploadRejection(event.payload.file, CERTIFICATE_EXTENSIONS) !== null,
    hasNoProvider: ({ event }) =>
      event.type === 'ON_UPLOAD_CERTIFICATE' && !event.payload.providerId,
  },
  actions: {
    alertRejectedFile: ({ event, system }) => {
      if (event.type !== 'ON_UPLOAD_CERTIFICATE') return;
      const rejection = getUploadRejection(event.payload.file, CERTIFICATE_EXTENSIONS);
      if (rejection) {
        sendAlert(system, {
          text: rejection.text,
          subText: rejection.subText,
          type: 'error',
          timeout: 6000,
        });
      }
    },
    alertUploadFailed: ({ event, system }) => {
      if (event.type !== 'ON_DOCUMENT_UPLOAD_ERROR') return;
      sendAlert(system, { ...getUploadErrorAlert(event.error), type: 'error', timeout: 6000 });
    },
    alertNotReady: ({ system }) => sendAlert(system, NOT_READY_ALERT),
    // The service takes UPLOAD_DOCUMENT only when idle and drops it otherwise, which would leave
    // this machine waiting for an outcome that never comes.
    startUpload: enqueueActions(({ enqueue, event, self, system }) => {
      if (event.type !== 'ON_UPLOAD_CERTIFICATE' || !event.payload.providerId) return;
      const documents = getDocumentsService(system);
      if (!documents?.getSnapshot().matches(DOCUMENTS_SERVICE_IDLE)) {
        sendAlert(system, NOT_READY_ALERT);
        enqueue.raise({ type: 'ON_UPLOAD_REFUSED' });
        return;
      }
      documents.send({
        type: 'UPLOAD_DOCUMENT',
        providerId: event.payload.providerId,
        file: event.payload.file,
        viewRef: self,
        values: buildCertificateUploadValues(event.payload.file),
      });
    }),
    alertUploaded: ({ system }) =>
      sendAlert(system, {
        text: 'Certificate uploaded',
        subText: 'Reading the policy details. They will appear in the table when ready.',
        type: 'success',
        timeout: 5000,
      }),
  },
}).createMachine({
  id: 'insuranceCertificateUpload',
  initial: '$_IDLE',
  context: {},
  states: {
    $_IDLE: {
      on: {
        ON_UPLOAD_CERTIFICATE: [
          { guard: 'isRejectedFile', actions: 'alertRejectedFile' },
          { guard: 'hasNoProvider', actions: 'alertNotReady' },
          { target: '$_UPLOADING', actions: 'startUpload' },
        ],
      },
    },
    $_UPLOADING: {
      on: {
        ON_DOCUMENT_UPLOADED: { target: '$_IDLE', actions: 'alertUploaded' },
        ON_DOCUMENT_UPLOAD_ERROR: { target: '$_IDLE', actions: 'alertUploadFailed' },
        ON_UPLOAD_REFUSED: '$_IDLE',
      },
    },
  },
});
